import React, { useState, useEffect } from 'react';
import { Registration, Tour } from '../types';
import AdminNavbar from '../components/admin/AdminNavbar';
import { getRegistrations, updateRegistrationStatus } from '../services/registrationService';
import { fetchAllToursForAdmin } from '../services/tourService';

interface TourOption {
  id: string;
  name: string;
}

const AdminRegistrationsPage: React.FC = () => {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState<Registration[]>([]);
  const [tours, setTours] = useState<Tour[]>([]);
  const [activeTourOptions, setActiveTourOptions] = useState<TourOption[]>([]);
  const [inactiveTourOptions, setInactiveTourOptions] = useState<TourOption[]>([]);
  const [selectedTourId, setSelectedTourId] = useState<string>('');
  const [selectedCompletedTourId, setSelectedCompletedTourId] = useState<string>('');
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [activeView, setActiveView] = useState<'pending' | 'completed'>('pending');
  const [completedRegistrations, setCompletedRegistrations] = useState<Registration[]>([]);
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  const [selectedCompletedRows, setSelectedCompletedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load regular tours
        const regularTours = await fetchAllToursForAdmin();
        setTours(regularTours);

        // Load only non-completed registrations (pending and cancelled)
        const registrationsData = await getRegistrations({ limit: 200 });
        // Filter out completed registrations from the main list
        const nonCompletedRegs = registrationsData.items.filter(reg => reg.status !== 'completed');
        setRegistrations(nonCompletedRegs);
        setFilteredRegistrations(nonCompletedRegs);

      } catch (err: any) {
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    // Separate tours by status
    const activeOptions: TourOption[] = tours
      .filter(tour => tour.status === 'active')
      .map(tour => ({
        id: tour.tourId,
        name: tour.tourName,
      }));

    const inactiveOptions: TourOption[] = tours
      .filter(tour => tour.status === 'inactive' || tour.status === 'sold_out')
      .map(tour => ({
        id: tour.tourId,
        name: tour.tourName,
      }));

    setActiveTourOptions(activeOptions);
    setInactiveTourOptions(inactiveOptions);
  }, [tours]);

  useEffect(() => {
    // Filter pending registrations by selected tour
    if (!selectedTourId) {
      setFilteredRegistrations(registrations);
    } else {
      const filtered = registrations.filter(reg => reg.tourId === selectedTourId);
      setFilteredRegistrations(filtered);
    }
  }, [selectedTourId, registrations]);

  // Filter completed registrations by selected tour
  const getFilteredCompletedRegistrations = () => {
    if (!selectedCompletedTourId) {
      return completedRegistrations;
    }
    return completedRegistrations.filter(reg => reg.tourId === selectedCompletedTourId);
  };

  const handleRegularTourFilterChange = (tourId: string) => {
    setSelectedTourId(tourId);
    setSelectedRows(new Set()); // Clear selection when filter changes
  };

  const handleCompletedRegularTourFilterChange = (tourId: string) => {
    setSelectedCompletedTourId(tourId);
    setSelectedCompletedRows(new Set()); // Clear selection when filter changes
  };

  const handleRowSelect = (registrationId: string) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(registrationId)) {
      newSelectedRows.delete(registrationId);
    } else {
      newSelectedRows.add(registrationId);
    }
    setSelectedRows(newSelectedRows);
  };

  const handleSelectAll = () => {
    if (selectedRows.size === filteredRegistrations.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredRegistrations.map(reg => reg.id)));
    }
  };

  const handleCompletedRowSelect = (registrationId: string) => {
    const newSelectedRows = new Set(selectedCompletedRows);
    if (newSelectedRows.has(registrationId)) {
      newSelectedRows.delete(registrationId);
    } else {
      newSelectedRows.add(registrationId);
    }
    setSelectedCompletedRows(newSelectedRows);
  };

  const handleSelectAllCompleted = () => {
    const filtered = getFilteredCompletedRegistrations();
    if (selectedCompletedRows.size === filtered.length) {
      setSelectedCompletedRows(new Set());
    } else {
      setSelectedCompletedRows(new Set(filtered.map(reg => reg.id)));
    }
  };

  const exportToCSV = (data: Registration[], filename: string) => {
    if (data.length === 0) {
      alert('No data to export');
      return;
    }

    // Get all possible fields from registrations
    const allFields = [
      'id', 'givenName', 'surname', 'phoneNumber', 'email', 'dateOfBirth', 
      'passportNumber', 'nationality', 'sex', 'address', 'passportIssueDate', 
      'passportExpiryDate', 'tourId', 'tourName', 'emergencyContact', 
      'dietaryRequirements', 'medicalConditions', 'specialRequests', 
      'status', 'createdAt', 'updatedAt'
    ];

    // Create CSV header
    const headers = allFields.join(',');
    
    // Create CSV rows
    const rows = data.map(registration => {
      return allFields.map(field => {
        const value = registration[field as keyof Registration];
        let stringValue = '';
        
        // Handle date fields specially
        if (field === 'dateOfBirth' || field === 'passportIssueDate' || 
            field === 'passportExpiryDate' || field === 'createdAt' || field === 'updatedAt') {
          stringValue = formatDateForCSV(value);
        } else if (value) {
          // Handle other fields - escape commas and quotes
          stringValue = String(value).replace(/,/g, ';').replace(/"/g, '""');
        }
        
        return `"${stringValue}"`;
      }).join(',');
    });

    // Combine header and rows
    const csvContent = [headers, ...rows].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportSelected = () => {
    if (selectedRows.size === 0) {
      alert('Please select rows to export');
      return;
    }
    
    const selectedData = filteredRegistrations.filter(reg => selectedRows.has(reg.id));
    const today = new Date().toISOString().split('T')[0];
    const filename = `selected_pending_${today}.csv`;
    exportToCSV(selectedData, filename);
  };

  const handleExportSelectedCompleted = () => {
    if (selectedCompletedRows.size === 0) {
      alert('Please select rows to export');
      return;
    }
    
    const filtered = getFilteredCompletedRegistrations();
    const selectedData = filtered.filter(reg => selectedCompletedRows.has(reg.id));
    const today = new Date().toISOString().split('T')[0];
    const filename = `selected_completed_${today}.csv`;
    exportToCSV(selectedData, filename);
  };

  const handleExportAll = () => {
    const today = new Date().toISOString().split('T')[0];
    if (activeView === 'pending') {
      const filename = `pending_registrations_${today}.csv`;
      exportToCSV(filteredRegistrations, filename);
    } else {
      const filtered = getFilteredCompletedRegistrations();
      if (filtered.length === 0) {
        alert('No completed registrations to export.');
        return;
      }
      const filename = `completed_registrations_${today}.csv`;
      exportToCSV(filtered, filename);
    }
  };

  const loadCompletedRegistrations = async () => {
    if (completedRegistrations.length > 0) {
      // Already loaded, just switch view
      return;
    }

    try {
      setLoadingCompleted(true);
      const completedData = await getRegistrations({ status: 'completed', limit: 200 });
      setCompletedRegistrations(completedData.items);
    } catch (err: any) {
      console.error('Error loading completed registrations:', err);
      setStatusMessage({ 
        type: 'error', 
        message: err.message || 'Failed to load completed registrations' 
      });
      setTimeout(() => setStatusMessage(null), 5000);
    } finally {
      setLoadingCompleted(false);
    }
  };

  const handleViewChange = async (view: 'pending' | 'completed') => {
    setActiveView(view);
    if (view === 'completed' && completedRegistrations.length === 0) {
      await loadCompletedRegistrations();
    }
  };

  const handleStatusUpdate = async (newStatus: 'pending' | 'completed' | 'cancelled', fromCompleted: boolean = false) => {
    const selectedSet = fromCompleted ? selectedCompletedRows : selectedRows;
    
    if (selectedSet.size === 0) {
      setStatusMessage({ type: 'error', message: 'Please select at least one registration to update' });
      setTimeout(() => setStatusMessage(null), 5000);
      return;
    }

    try {
      setUpdatingStatus(true);
      setStatusMessage(null);

      // Update all selected registrations
      const updatePromises = Array.from(selectedSet).map(registrationId =>
        updateRegistrationStatus(registrationId, newStatus)
      );

      await Promise.all(updatePromises);

      // Refresh the registrations data (excluding completed)
      const registrationsData = await getRegistrations({ limit: 200 });
      const nonCompletedRegs = registrationsData.items.filter(reg => reg.status !== 'completed');
      setRegistrations(nonCompletedRegs);
      setFilteredRegistrations(
        selectedTourId 
          ? nonCompletedRegs.filter(reg => reg.tourId === selectedTourId)
          : nonCompletedRegs
      );

      // If completed registrations are loaded, refresh them too
      if (completedRegistrations.length > 0 || newStatus === 'completed') {
        const completedData = await getRegistrations({ status: 'completed', limit: 200 });
        setCompletedRegistrations(completedData.items);
      }

      // Clear selection and show success message
      setSelectedRows(new Set());
      setSelectedCompletedRows(new Set());
      setStatusMessage({ 
        type: 'success', 
        message: `Successfully updated ${updatePromises.length} registration(s) to ${newStatus}` 
      });
      setTimeout(() => setStatusMessage(null), 5000);

    } catch (err: any) {
      console.error('Status update error:', err);
      setStatusMessage({ 
        type: 'error', 
        message: err.message || 'Failed to update registration status' 
      });
      setTimeout(() => setStatusMessage(null), 5000);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDateForCSV = (dateString: string | any) => {
    try {
      let date;
      
      // Handle Firebase Firestore Timestamp objects
      if (dateString && typeof dateString === 'object') {
        // Check if it's a Firestore Timestamp with toDate method
        if (typeof dateString.toDate === 'function') {
          date = dateString.toDate();
        }
        // Check if it's a Firestore Timestamp with seconds property
        else if (dateString.seconds && typeof dateString.seconds === 'number') {
          date = new Date(dateString.seconds * 1000);
        }
        // Check if it's already a Date object
        else if (dateString instanceof Date) {
          date = dateString;
        }
        else {
          console.warn('Unknown date object format:', dateString);
          return '';
        }
      } else if (dateString) {
        // Regular date string or ISO string from server
        date = new Date(dateString);
      } else {
        return '';
      }
      
      // Check if date is valid
      if (!date || isNaN(date.getTime())) {
        console.warn('Invalid date:', dateString);
        return '';
      }
      
      // Format for Excel: MM/DD/YYYY format
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date for CSV:', error, dateString);
      return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--secondary-pearl)]">
        <AdminNavbar />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--primary-deep-blue)] mx-auto mb-4"></div>
            <p className="body-regular text-[var(--secondary-slate)]">Loading registrations...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--secondary-pearl)]">
        <AdminNavbar />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <strong className="font-bold">Error:</strong> {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--secondary-pearl)]">
      <AdminNavbar />
      <div className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="heading-primary text-[var(--primary-deep-blue)] mb-2">
                Registrations Management
              </h1>
              <p className="body-regular text-[var(--secondary-slate)] mb-4">
                View and manage tour registrations
              </p>
              
              {/* View Toggle Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => handleViewChange('pending')}
                  className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 ${
                    activeView === 'pending'
                      ? 'btn-tab-active-amber'
                      : 'btn-tab-inactive'
                  }`}
                >
                  Pending
                </button>
                <button
                  onClick={() => handleViewChange('completed')}
                  disabled={loadingCompleted}
                  className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 ${
                    activeView === 'completed'
                      ? 'btn-tab-active-green'
                      : 'btn-tab-inactive'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loadingCompleted ? 'Loading...' : 'Completed'}
                </button>
              </div>
            </div>

            {/* Filters and Actions */}
            <div className="card mb-6">
              <div className="card-content">
                <div className="flex flex-col gap-4">
                  {/* Tour Filter and Export Row */}
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    {/* Tour Filters */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      {activeView === 'pending' ? (
                        <div className="flex items-center gap-2">
                          <label htmlFor="regularTourFilter" className="body-medium text-[var(--primary-deep-blue)] font-semibold whitespace-nowrap">
                            Tour:
                          </label>
                          <select
                            id="regularTourFilter"
                            value={selectedTourId}
                            onChange={(e) => handleRegularTourFilterChange(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--primary-deep-blue)] focus:border-transparent"
                          >
                            <option value="">All</option>
                            {activeTourOptions.map(tour => (
                              <option key={tour.id} value={tour.id}>
                                {tour.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <label htmlFor="completedRegularTourFilter" className="body-medium text-[var(--primary-deep-blue)] font-semibold whitespace-nowrap">
                            Tour:
                          </label>
                          <select
                            id="completedRegularTourFilter"
                            value={selectedCompletedTourId}
                            onChange={(e) => handleCompletedRegularTourFilterChange(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--primary-deep-blue)] focus:border-transparent"
                          >
                            <option value="">All</option>
                            {inactiveTourOptions.map(tour => (
                              <option key={tour.id} value={tour.id}>
                                {tour.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    {/* Export Actions */}
                    <div className="flex items-center gap-3 ml-auto">
                      {activeView === 'pending' ? (
                        <button
                          onClick={handleExportSelected}
                          disabled={selectedRows.size === 0}
                          className="btn-action-primary"
                        >
                          Export Selected ({selectedRows.size})
                        </button>
                      ) : (
                        <button
                          onClick={handleExportSelectedCompleted}
                          disabled={selectedCompletedRows.size === 0}
                          className="btn-action-primary"
                        >
                          Export Selected ({selectedCompletedRows.size})
                        </button>
                      )}
                      <button
                        onClick={handleExportAll}
                        className="btn-action-green"
                      >
                        Export All ({activeView === 'pending' ? filteredRegistrations.length : getFilteredCompletedRegistrations().length})
                      </button>
                    </div>
                  </div>

                  {/* Status Update Buttons */}
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                    {activeView === 'pending' ? (
                      <>
                        <button
                          onClick={() => handleStatusUpdate('completed', false)}
                          disabled={selectedRows.size === 0 || updatingStatus}
                          className="btn-action-green"
                        >
                          {updatingStatus ? 'Updating...' : 'Mark as Completed'}
                        </button>
                        <span className="text-sm text-[var(--secondary-slate)] ml-2">
                          {selectedRows.size > 0 ? `${selectedRows.size} selected` : 'Select registrations to mark as completed'}
                        </span>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleStatusUpdate('pending', true)}
                          disabled={selectedCompletedRows.size === 0 || updatingStatus}
                          className="btn-action-amber"
                        >
                          {updatingStatus ? 'Updating...' : 'Mark as Pending'}
                        </button>
                        <span className="text-sm text-[var(--secondary-slate)] ml-2">
                          {selectedCompletedRows.size > 0 ? `${selectedCompletedRows.size} selected` : 'Select registrations to move back to pending'}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Status Message */}
                  {statusMessage && (
                    <div
                      className={`px-4 py-3 rounded-lg ${
                        statusMessage.type === 'success'
                          ? 'bg-green-100 border border-green-400 text-green-700'
                          : 'bg-red-100 border border-red-400 text-red-700'
                      }`}
                    >
                      <p className="font-medium">{statusMessage.message}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Registrations Table - Pending View */}
            {activeView === 'pending' && (
              <>
                <div className="card">
                  <div className="card-content p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-solid-amber text-white">
                            <th className="border border-gray-300 px-4 py-3 text-left">
                              <input
                                type="checkbox"
                                checked={filteredRegistrations.length > 0 && selectedRows.size === filteredRegistrations.length}
                                onChange={handleSelectAll}
                                className="rounded border-gray-300"
                              />
                            </th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Given Name</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Surname</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Email</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Phone</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Passport</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Nationality</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Tour Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRegistrations.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="border border-gray-300 px-4 py-8 text-center text-gray-500">
                                No pending registrations found
                              </td>
                            </tr>
                          ) : (
                            filteredRegistrations.map((registration) => (
                              <tr key={registration.id} className="hover:bg-amber-50">
                                <td className="border border-gray-300 px-4 py-3">
                                  <input
                                    type="checkbox"
                                    checked={selectedRows.has(registration.id)}
                                    onChange={() => handleRowSelect(registration.id)}
                                    className="rounded border-gray-300"
                                  />
                                </td>
                                <td className="border border-gray-300 px-4 py-3">{registration.givenName}</td>
                                <td className="border border-gray-300 px-4 py-3">{registration.surname}</td>
                                <td className="border border-gray-300 px-4 py-3">{registration.email}</td>
                                <td className="border border-gray-300 px-4 py-3">{registration.phoneNumber}</td>
                                <td className="border border-gray-300 px-4 py-3">{registration.passportNumber}</td>
                                <td className="border border-gray-300 px-4 py-3">{registration.nationality}</td>
                                <td className="border border-gray-300 px-4 py-3">{registration.tourName}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="mt-4 text-center">
                  <p className="body-regular text-[var(--secondary-slate)]">
                    Showing {filteredRegistrations.length} of {registrations.length} pending registrations
                    {selectedRows.size > 0 && ` • ${selectedRows.size} selected`}
                  </p>
                </div>
              </>
            )}

            {/* Registrations Table - Completed View */}
            {activeView === 'completed' && (
              <>
                <div className="card">
                  <div className="card-content p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-solid-green text-white">
                            <th className="border border-gray-300 px-4 py-3 text-left">
                            <input
                              type="checkbox"
                              checked={getFilteredCompletedRegistrations().length > 0 && selectedCompletedRows.size === getFilteredCompletedRegistrations().length}
                              onChange={handleSelectAllCompleted}
                              className="rounded border-gray-300"
                            />
                            </th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Given Name</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Surname</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Email</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Phone</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Passport</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Nationality</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Tour Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getFilteredCompletedRegistrations().length === 0 ? (
                            <tr>
                              <td colSpan={8} className="border border-gray-300 px-4 py-8 text-center text-gray-500">
                                No completed registrations found
                              </td>
                            </tr>
                          ) : (
                            getFilteredCompletedRegistrations().map((registration) => (
                              <tr key={registration.id} className="hover:bg-green-50">
                                <td className="border border-gray-300 px-4 py-3">
                                  <input
                                    type="checkbox"
                                    checked={selectedCompletedRows.has(registration.id)}
                                    onChange={() => handleCompletedRowSelect(registration.id)}
                                    className="rounded border-gray-300"
                                  />
                                </td>
                                <td className="border border-gray-300 px-4 py-3">{registration.givenName}</td>
                                <td className="border border-gray-300 px-4 py-3">{registration.surname}</td>
                                <td className="border border-gray-300 px-4 py-3">{registration.email}</td>
                                <td className="border border-gray-300 px-4 py-3">{registration.phoneNumber}</td>
                                <td className="border border-gray-300 px-4 py-3">{registration.passportNumber}</td>
                                <td className="border border-gray-300 px-4 py-3">{registration.nationality}</td>
                                <td className="border border-gray-300 px-4 py-3">{registration.tourName}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="mt-4 text-center">
                  <p className="body-regular text-[var(--secondary-slate)]">
                    Showing {getFilteredCompletedRegistrations().length} of {completedRegistrations.length} completed registrations
                    {selectedCompletedRows.size > 0 && ` • ${selectedCompletedRows.size} selected`}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminRegistrationsPage;
